import get from 'lodash/get';
import React, { useState, useEffect, FC } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import range from 'lodash/range';
import isNull from 'lodash/isNull';

import { DialogTitleWithClose, RangeSlider, GroupedSelect } from 'ui';
import { useGroupedAutocompleteStyles } from 'shared/styles/groupedAutocompleteStyles/groupedAutocompleteStyles';
import { useRegions } from 'shared/hooks';
import { getSortedFlattenedIndustries } from 'selectors/common';
import { CreditReviewLevels, CreditReviewSpec } from 'shared/types/creditReview';
import { SearchQuery } from 'shared/types/portfolios';

type Values = {
  companyName: string;
  countries: any[];
  industries: any[];
  creditScore: any[];
  sectorRiskLevel: any[];
  businessClimate: any[];
  countryRiskAssessment: any[];
};

type CreditReviewEntitiesFilterModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: Values) => void;
  searchQuery?: SearchQuery;
  initialFilter: any;
  filterableIndustries?: any[] | null;
  allowEmptyIndustriesFilter?: boolean;
  creditReviewLevels: CreditReviewLevels;
};

const CreditReviewEntitiesFilterModal: FC<CreditReviewEntitiesFilterModalProps> = ({
  open,
  onClose,
  onSubmit,
  searchQuery,
  initialFilter = {},
  filterableIndustries = null,
  allowEmptyIndustriesFilter = true,
  creditReviewLevels,
}: CreditReviewEntitiesFilterModalProps) => {
  const groupedAutocompleteClasses = useGroupedAutocompleteStyles();
  const { t } = useTranslation();
  const industriesData = useSelector(getSortedFlattenedIndustries);
  const { data: creditReviewSpecs } = useSelector(({ shared }: any) => shared.creditReviewSpecs);
  const regions = useRegions(searchQuery?.query);

  const mapRatingLevels = (items: any[]) =>
    items.map((level, index) => ({
      value: index + 1,
      label: level,
    }));

  const sectorRiskLevels = mapRatingLevels(creditReviewLevels.sectorRiskLevel);
  const businessClimateLevels = mapRatingLevels(creditReviewLevels.businessClimate);
  const countryRiskAssessmentLevels = mapRatingLevels(creditReviewLevels.countryRiskAssessment);

  const creditScoreValues = range(11).map((level, index) => {
    const levelStr = level.toString();
    const spec = creditReviewSpecs?.find(({ score }: CreditReviewSpec) => score === level);
    const pod = spec?.pd;
    return {
      value: index + 1,
      label: !isNull(pod) ? `${levelStr} (${pod}%)` : levelStr,
    };
  });

  const [values, setValues] = useState<Values>({
    companyName: '',
    countries: [],
    industries: [],
    creditScore: creditScoreValues.length
      ? [creditScoreValues[0].value, creditScoreValues[creditScoreValues.length - 1].value]
      : [],
    sectorRiskLevel: sectorRiskLevels.length
      ? [sectorRiskLevels[0].value, sectorRiskLevels[sectorRiskLevels.length - 1].value]
      : [],

    businessClimate: businessClimateLevels.length
      ? [
          businessClimateLevels[0].value,
          businessClimateLevels[businessClimateLevels.length - 1].value,
        ]
      : [],

    countryRiskAssessment: countryRiskAssessmentLevels.length
      ? [
          countryRiskAssessmentLevels[0].value,
          countryRiskAssessmentLevels[countryRiskAssessmentLevels.length - 1].value,
        ]
      : [],
  });

  const [industries, setIndustries] = useState<any[]>([]);

  useEffect(() => {
    setValues({
      ...values,
      ...initialFilter,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilter]);

  useEffect(() => {
    setIndustries(filterableIndustries || industriesData);
  }, [industriesData, filterableIndustries]);

  const handleChange = (name: string) => (value: unknown) => {
    setValues({
      ...values,
      [name]: value,
    });
  };

  const handleIndustriesChange = (_event: React.ChangeEvent<any>, sectors: any[]) => {
    let newSectors = sectors;
    if (!allowEmptyIndustriesFilter && !sectors.length) {
      const initialIndustry = get(initialFilter, 'industries[0]');
      newSectors = initialIndustry ? [initialIndustry] : [];
    }
    setValues({
      ...values,
      industries: newSectors,
    });
  };

  const handleSubmit = () => {
    const { creditScore, sectorRiskLevel, businessClimate, countryRiskAssessment } = values;

    const cleanedValues = {
      ...values,
      countries: values.countries.map(({ iso }) => iso),
      sectors: values.industries.map(({ name }) => name),
      creditScoreFrom: encodeURIComponent(creditScoreValues[creditScore[0] - 1].value),
      creditScoreTo: encodeURIComponent(creditScoreValues[creditScore[1] - 1].value),
      sectorRiskLevelFrom: encodeURIComponent(sectorRiskLevels[sectorRiskLevel[0] - 1].label),
      sectorRiskLevelTo: encodeURIComponent(sectorRiskLevels[sectorRiskLevel[1] - 1].label),
      businessClimateFrom: encodeURIComponent(businessClimateLevels[businessClimate[0] - 1].label),
      businessClimateTo: encodeURIComponent(businessClimateLevels[businessClimate[1] - 1].label),
      countryRiskAssessmentFrom: encodeURIComponent(
        countryRiskAssessmentLevels[countryRiskAssessment[0] - 1].label,
      ),
      countryRiskAssessmentTo: encodeURIComponent(
        countryRiskAssessmentLevels[countryRiskAssessment[1] - 1].label,
      ),
    };

    onSubmit(cleanedValues);
  };

  const handleSectorRiskChange = (_event: React.ChangeEvent<any>, newValues: any[]) => {
    setValues({
      ...values,
      sectorRiskLevel: newValues,
    });
  };

  const handleBusinessClimateChange = (_event: React.ChangeEvent<any>, newValues: any[]) => {
    setValues({
      ...values,
      businessClimate: newValues,
    });
  };

  const handleCountryRiskAssessmentChange = (_event: React.ChangeEvent<any>, newValues: any[]) => {
    setValues({
      ...values,
      countryRiskAssessment: newValues,
    });
  };

  const handleCreditScoreChange = (_event: React.ChangeEvent<any>, newValues: any[]) => {
    setValues({
      ...values,
      creditScore: newValues,
    });
  };

  const isValueRemovable = () => {
    return allowEmptyIndustriesFilter || values.industries.length > 1;
  };

  return (
    <Dialog fullWidth open={open}>
      <DialogTitleWithClose onClose={onClose}>{t('Apply filters')}</DialogTitleWithClose>
      <DialogContent>
        <Box px={2}>
          <TextField
            type="text"
            label={t('ENTITY.NAME')}
            fullWidth
            value={values.companyName}
            onChange={event => handleChange('companyName')(event.target.value)}
          />
          <GroupedSelect
            multiple
            options={regions.data}
            value={values.countries}
            label={t('Country')}
            onChange={handleChange('countries')}
            getOptionLabel={(option: any) => option.name}
            groupBy={(option: any) => option.continent}
          />
          {false && (
            <Autocomplete
              multiple
              options={industries}
              onChange={handleIndustriesChange}
              getOptionLabel={option => option.name}
              groupBy={option => option.industryName}
              value={values.industries}
              renderInput={params => (
                <TextField {...params} label={t('Sectors')} variant="outlined" />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    disabled={!isValueRemovable()}
                  />
                ))
              }
              classes={groupedAutocompleteClasses}
            />
          )}
          <Box mt={2} mb={1}>
            <RangeSlider
              label={t('Credit assessment')}
              options={creditScoreValues}
              values={values.creditScore}
              onChange={handleCreditScoreChange}
            />
          </Box>
          {false && (
            <Box mt={2} mb={1}>
              <RangeSlider
                label={t('Industry Risk')}
                options={sectorRiskLevels}
                values={values.sectorRiskLevel}
                onChange={handleSectorRiskChange}
              />
            </Box>
          )}
          {false && (
            <Box mt={2} mb={1}>
              <RangeSlider
                label={t('Business Climate')}
                options={businessClimateLevels}
                values={values.businessClimate}
                onChange={handleBusinessClimateChange}
              />
            </Box>
          )}
          <Box mt={2} mb={1}>
            <RangeSlider
              label={t('Country Risk Assessment')}
              options={countryRiskAssessmentLevels}
              values={values.countryRiskAssessment}
              onChange={handleCountryRiskAssessmentChange}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={handleSubmit}>
          {t('Apply filters')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreditReviewEntitiesFilterModal;
